import { Box, Fade, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../components/atomic';
import { EarlyAccessForm } from '../components/forms';
import SEO from '../components/SEO';
import { StyledButton } from '../components/ui';
import { ContentPageLayout } from '../layouts';

/**
 * Benefit card data structure
 * @typedef {Object} BenefitCard
 * @property {string} logo - Path to logo image
 * @property {string} logoAlt - Alt text for logo image
 * @property {React.ReactNode} description - Benefit description (supports JSX with bold text)
 */

/**
 * Early Access Program page
 * Focused lead-capture page for EllaFin Early Access Program
 *
 * Redesigned to match the sophisticated visual aesthetic of the homepage with:
 * - Solid hero background
 * - Clean benefit cards with hover effects
 * - Social proof indicators
 * - Clear call-to-action
 * - Scroll-triggered animations
 *
 * @returns {JSX.Element} Early Access Program page
 */
function EarlyAccessProgram() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});
  const theme = useTheme();
  const _isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /** @type {BenefitCard[]} */
  const benefits = [
    {
      logo: `${process.env.PUBLIC_URL}/images/ella-fin-logo-cropped.jpg`,
      logoAlt: 'EllaFin logo',
      description: (
        <>
          Experience <strong>Department Tier Features</strong> at Individual
          Tier Price.
        </>
      ),
    },
    {
      logo: `${process.env.PUBLIC_URL}/images/aws-logo.png`,
      logoAlt: 'AWS logo',
      description: (
        <>
          <strong>AWS Credits</strong> upon completion of a POV (proof of value)
        </>
      ),
    },
    {
      logo: `${process.env.PUBLIC_URL}/images/aws-marketplace-logo.png`,
      logoAlt: 'AWS Marketplace logo',
      description: <>Lock-in Team Tier Benefits at Individual Tier Pricing</>,
    },
    {
      logo: `${process.env.PUBLIC_URL}/images/finops-certified-ai.png`,
      logoAlt: 'FinOps Certified – FinOps for AI badge',
      description: <>FinOps Foundation $500 Discount Code</>,
    },
  ];

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const observers = {};

    Object.keys(sectionRefs.current).forEach((key) => {
      if (sectionRefs.current[key]) {
        observers[key] = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => ({ ...prev, [key]: true }));
            }
          },
          { threshold: 0.2 }
        );
        observers[key].observe(sectionRefs.current[key]);
      }
    });

    return () => {
      Object.values(observers).forEach((observer) => {
        observer.disconnect();
      });
    };
  }, []);

  /**
   * Register a section ref for intersection observer
   * @param {string} sectionName - Section identifier
   * @returns {Function} Ref callback
   */
  const registerRef = (sectionName) => (el) => {
    sectionRefs.current[sectionName] = el;
  };

  return (
    <ContentPageLayout backgroundColor={theme.palette.background.section}>
      <SEO
        title="Early Access Program - Be Among the First"
        description="Join EllaFin's early access program. Benefits include Department Tier features at Individual pricing, AWS credits, lock-in pricing for 3 years, and a FinOps Foundation training discount."
        path="/early-access"
        keywords="early access, FinOps early adopter, AWS credits, EllaFin waitlist, cloud cost management beta, FinOps training"
      />

      {/* Section 1: Hero */}
      <Box
        component="section"
        aria-label="Early access hero"
        sx={{
          position: 'relative',
          py: { xs: 8, md: 10 },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          backgroundColor: theme.custom.footerBackground || '#13152b',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: { xs: 4, md: 6 },
              py: { xs: 4, md: 8 },
            }}
          >
            {/* Left Content - Text */}
            <Box
              sx={{
                flex: '1 1 55%',
                maxWidth: { xs: '100%', md: '60%' },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              {/* Green accent bar */}
              <Box
                sx={{
                  width: '60px',
                  height: '4px',
                  backgroundColor: theme.palette.secondary.main,
                  mb: 3,
                  mx: { xs: 'auto', md: 0 },
                }}
              />

              {/* Headline */}
              <Fade in={isLoaded} timeout={800}>
                <Box>
                  <Heading
                    variant="h1"
                    component="h1"
                    sx={{
                      fontSize: {
                        xs: '2.25rem',
                        sm: '2.75rem',
                        md: '3.25rem',
                        lg: '3.75rem',
                      },
                      fontWeight: 700,
                      lineHeight: 1.1,
                      mb: 3,
                      color: '#ffffff',
                      letterSpacing: '-0.02em',
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    Be Among the First
                  </Heading>
                </Box>
              </Fade>

              {/* Subtitle */}
              <Fade in={isLoaded} timeout={1000}>
                <Box>
                  <Text
                    sx={{
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      color: alpha('#ffffff', 0.7),
                      lineHeight: 1.7,
                      mb: 4,
                    }}
                  >
                    Be part of the Finance-First{' '}
                    <span
                      style={{
                        color: theme.palette.secondary.main,
                        fontWeight: 900,
                      }}
                    >
                      Fin
                    </span>
                    Ops revolution. Join our early access program and help shape
                    the future of AWS cost management.
                  </Text>
                </Box>
              </Fade>

              <Fade in={isLoaded} timeout={1200}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    mb: 4,
                  }}
                >
                  <StyledButton
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      document
                        .getElementById('cta')
                        ?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      color: '#ffffff',
                    }}
                  >
                    Get Started
                  </StyledButton>
                  <StyledButton
                    variant="contained"
                    size="large"
                    onClick={() => {
                      document
                        .getElementById('benefits')
                        ?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      backgroundColor: '#f4f0eb',
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: '#ebe5dd',
                      },
                    }}
                  >
                    See Benefits
                  </StyledButton>
                </Box>
              </Fade>
            </Box>

            {/* Right Content - EllaFin Logo + AWS Badge */}
            <Fade in={isLoaded} timeout={1000}>
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  flex: '1 1 35%',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '280px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/images/ella-fin-logo.jpg`}
                    alt="EllaFin™ elephant logo"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '12px',
                    }}
                  />
                </Box>
              </Box>
            </Fade>
          </Box>

          {/* AWS Marketplace Badge */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: { xs: 4, md: 6 },
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/aws-marketplace-badge.png`}
              alt="Available in AWS Marketplace"
              sx={{
                height: { xs: 80, md: 95 },
                filter: 'brightness(0) invert(1)',
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Section 2: Benefits - Bento Grid (Below the fold) */}
      <Box
        id="benefits"
        ref={registerRef('benefits')}
        component="section"
        aria-label="Early access benefits"
        sx={{
          py: { xs: 10, md: 14 },
          backgroundColor: theme.custom.footerBackground || '#13152b',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              textAlign: 'center',
              mb: { xs: 6, md: 8 },
              opacity: visibleSections.benefits ? 1 : 0,
              transform: visibleSections.benefits
                ? 'translateY(0)'
                : 'translateY(20px)',
              transition: 'all 0.5s ease',
            }}
          >
            <Heading
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 700,
                color: '#ffffff',
                mb: 2,
              }}
            >
              Early Access Benefits
            </Heading>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 3,
            }}
          >
            {benefits.map((benefit, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: alpha('#ffffff', 0.08),
                  p: 4,
                  borderRadius: '20px',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
                  border: `1px solid ${alpha('#ffffff', 0.15)}`,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  opacity: visibleSections.benefits ? 1 : 0,
                  transform: visibleSections.benefits
                    ? 'translateY(0)'
                    : 'translateY(20px)',
                  transition: `all 0.5s ease ${150 + index * 100}ms`,
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.02)',
                    boxShadow: '0 12px 40px rgba(125, 144, 26, 0.12)',
                    borderColor: alpha('#ffffff', 0.3),
                    backgroundColor: alpha('#ffffff', 0.12),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    backgroundColor: '#ffffff',
                    p: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={benefit.logo}
                    alt={benefit.logoAlt}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Text
                  sx={{
                    color: alpha('#ffffff', 0.85),
                    lineHeight: 1.7,
                    fontSize: '1rem',
                    '& strong': {
                      color: '#ffffff',
                      fontWeight: 700,
                    },
                  }}
                >
                  {benefit.description}
                </Text>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Section 3: CTA Card */}
      <Box
        id="cta"
        ref={registerRef('cta')}
        component="section"
        aria-label="Join early access"
        sx={{
          py: { xs: 10, md: 14 },
          // Darker purple for better contrast against footer
          backgroundColor: theme.custom.footerBackground,
          position: 'relative',
          overflow: 'hidden',
          // Top border accent
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundColor: theme.palette.secondary.main,
          },
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
              opacity: visibleSections.cta ? 1 : 0,
              transform: visibleSections.cta
                ? 'translateY(0)'
                : 'translateY(30px)',
              transition: 'all 0.8s ease',
            }}
          >
            <Heading
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 700,
                color: '#ffffff',
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              Ready to Experience EllaFin™?
            </Heading>

            <Text
              sx={{
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: '500px',
                mx: 'auto',
                mb: 5,
                lineHeight: 1.7,
              }}
            >
              Sign up to learn more about joining the early access program.
            </Text>

            {/* ActiveCampaign Early Access Form #19 */}
            <EarlyAccessForm />

            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.9rem',
                mt: 4,
              }}
            >
              No commitment required. Let's start a conversation.
            </Typography>
          </Box>
        </Container>
      </Box>
    </ContentPageLayout>
  );
}

export default EarlyAccessProgram;
